/* Add the optional Dock without changing the native M1 purchase geometry. */
(() => {
  'use strict';
  if (window.NexDockLayout) return;

  const rect = element => {
    const box = element.getBoundingClientRect();
    return { x: box.x, y: box.y, width: box.width, height: box.height,
      right: box.right, bottom: box.bottom };
  };
  const overlaps = (a, b) => a.x < b.right && a.right > b.x &&
    a.y < b.bottom && a.bottom > b.y;

  class NexDockLayout {
    constructor(host) {
      this.host = host;
      this.eligible = false;
      this.mode = null;
      this.metrics = null;
      this.destroyed = false;
      this.styles = new Map();
      this.cleanup = [];
      this.frame = 0;
      this.viewportTimer = 0;
      this.running = false;
      this.compactRating = null;
      this.safeArea = document.createElement('span');
      this.safeArea.className = 'm1-dock-safe-area';
      this.safeArea.setAttribute('aria-hidden', 'true');
      host.append(this.safeArea);

      const listen = (target, event, handler, options) => {
        if (!target) return;
        target.addEventListener(event, handler, options);
        this.cleanup.push(() => target.removeEventListener(event, handler, options));
      };
      const queue = () => this.schedule();
      const viewportSettled = () => {
        clearTimeout(this.viewportTimer);
        this.viewportTimer = setTimeout(() => {
          this.viewportTimer = 0;
          queue();
        }, 180);
      };
      listen(window, 'resize', viewportSettled, { passive: true });
      listen(window, 'orientationchange', viewportSettled, { passive: true });
      listen(window, 'pageshow', queue, { passive: true });
      listen(window.visualViewport, 'resize', viewportSettled, { passive: true });
      listen(window.visualViewport, 'scroll', viewportSettled, { passive: true });
      listen(document.fonts, 'loadingdone', queue);
      document.fonts?.ready.then(() => { if (!this.destroyed) this.schedule(); });

      this.observer = new MutationObserver(records => {
        if (records.some(record => record.target !== this.compactRating &&
          (record.target === host || record.target.matches?.(
            '[data-m1-dock-row], .gdab__checkout-error, nex-preorder-notify')))) queue();
      });
      this.observer.observe(host, { subtree: true, attributes: true,
        attributeFilter: ['data-gdab-state', 'data-inventory-state', 'data-gdab-closing', 'hidden'] });
      if (typeof ResizeObserver === 'function') {
        this.resizeObserver = new ResizeObserver(queue);
        [host, host.querySelector('.gdab__confirm-row'), document.querySelector('.site-header')]
          .filter(Boolean).forEach(element => this.resizeObserver.observe(element));
      }
    }

    schedule() {
      if (this.destroyed || this.frame || this.viewportTimer) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.refresh(this.eligible);
      });
    }

    // Restore individual properties, never another component's entire style attribute.
    set(element, property, value) {
      let owned = this.styles.get(element);
      if (!owned) this.styles.set(element, owned = new Map());
      if (!owned.has(property)) owned.set(property, {
        value: element.style.getPropertyValue(property),
        priority: element.style.getPropertyPriority(property), written: null, writtenPriority: '',
      });
      element.style.setProperty(property, value);
      owned.get(property).written = element.style.getPropertyValue(property);
      owned.get(property).writtenPriority = element.style.getPropertyPriority(property);
    }

    restore() {
      for (const [element, properties] of this.styles) {
        for (const [property, old] of properties) {
          // A newer outside owner wins if it changed the same property.
          if (element.style.getPropertyValue(property) !== old.written ||
            element.style.getPropertyPriority(property) !== old.writtenPriority) continue;
          if (old.value) element.style.setProperty(property, old.value, old.priority);
          else element.style.removeProperty(property);
        }
      }
      this.styles.clear();
      this.host.removeAttribute('data-m1-dock-layout');
      if (this.compactRating) this.compactRating.hidden = true;
    }

    nodes() {
      const query = selector => this.host.querySelector(selector);
      return {
        panel: query('.gdab__panel'), product: query('.gdab__product-shell'),
        visual: query('.gdab__product-visual'), image: query('.gdab__product-image'),
        promises: query('.gdab__promises'), close: query('.gdab__panel-close'),
        proof: query('.gdab__review-proof'), summary: query('.gdab__review-summary'),
        quote: query('.gdab__review-copy'), cta: query('.gdab__confirm-row'),
        dock: query('[data-m1-dock-row]'), header: document.querySelector('.site-header'),
      };
    }

    showCompactRating(nodes) {
      if (!nodes.summary) return;
      if (!this.compactRating) {
        this.compactRating = document.createElement('p');
        this.compactRating.className = 'm1-dock-compact-rating';
      }
      if (this.compactRating.parentNode !== nodes.panel) nodes.panel.append(this.compactRating);
      const original = nodes.summary.textContent.replace(/\s+/g, ' ').trim();
      const rating = original.match(/\d+(?:[.,]\d+)?/)?.[0] || '';
      const count = original.match(/·\s*([\d,]+)/)?.[1] || '';
      this.compactRating.textContent = rating
        ? `★ ${rating}${count ? ` · ${count} reviews` : ''}` : original;
      this.compactRating.setAttribute('aria-label', original);
      this.compactRating.hidden = false;

      const glyphs = [];
      const walker = document.createTreeWalker(nodes.promises, NodeFilter.SHOW_TEXT);
      let node;
      while ((node = walker.nextNode())) {
        if (!node.textContent.trim()) continue;
        const range = document.createRange();
        range.selectNodeContents(node);
        glyphs.push(...range.getClientRects());
      }
      const label = rect(this.compactRating);
      if (overlaps(label, rect(nodes.close)) || glyphs.some(glyph => overlaps(label, glyph))) {
        this.compactRating.textContent = rating ? `★ ${rating}` : original;
        this.set(this.compactRating, 'left', '12px');
      }
    }

    refresh(eligible = this.eligible) {
      if (this.destroyed) return null;
      this.eligible = Boolean(eligible);
      if (this.running) { this.schedule(); return this.metrics; }
      if (this.frame) { cancelAnimationFrame(this.frame); this.frame = 0; }
      if (this.viewportTimer) { clearTimeout(this.viewportTimer); this.viewportTimer = 0; }
      // Keep the existing iris/return animation's measured geometry until it ends.
      if (this.host._closing || this.host.hasAttribute('data-gdab-closing')) return this.metrics;
      this.running = true;
      try {
        this.restore();
        const nodes = this.nodes();
        const active = this.eligible && this.host.isConnected && this.host.state === 'panel' &&
          nodes.dock && !nodes.dock.hidden;
        const required = ['panel', 'product', 'visual', 'image', 'promises', 'close', 'proof', 'cta'];
        if (!active || required.some(key => !nodes[key])) {
          this.mode = null;
          this.metrics = { mode: 'native', eligible: this.eligible };
          return this.metrics;
        }
        const before = Object.fromEntries(required.map(key => [key, rect(nodes[key])]));
        if (!before.panel.width || !before.panel.height) return this.metrics;
        const vv = window.visualViewport;
        const safe = getComputedStyle(this.safeArea);
        const viewport = { width: window.innerWidth, height: window.innerHeight,
          top: vv?.offsetTop || 0, visibleHeight: vv?.height || window.innerHeight,
          safeTop: parseFloat(safe.paddingTop) || 0 };
        const header = nodes.header ? rect(nodes.header) : null;
        const headerVisible = header && header.height > 0 && header.bottom > viewport.top &&
          getComputedStyle(nodes.header).visibility !== 'hidden';
        const desiredTop = Math.max(viewport.top + viewport.safeTop + 12,
          headerVisible ? header.bottom + 8 : viewport.top + 12);
        // Existing compact layouts already approach the header; never add overlap.
        const topLimit = Math.min(before.panel.y, desiredTop);
        const growthRoom = Math.max(0, before.panel.y - topLimit);
        const headerSlack = Math.max(0, before.promises.y - before.close.bottom - 8);

        this.host.setAttribute('data-m1-dock-layout', 'measuring');
        this.set(nodes.dock, 'left', `${before.product.x - before.panel.x}px`);
        this.set(nodes.dock, 'width', `${before.product.width}px`);
        const rowHeight = rect(nodes.dock).height;
        const proofStyle = getComputedStyle(nodes.proof);
        const contentBottom = Math.max(0, ...[nodes.summary, nodes.quote]
          .filter(element => element && getComputedStyle(element).display !== 'none')
          .map(element => {
            const box = rect(element);
            const top = element.offsetParent === nodes.proof
              ? element.offsetTop : box.y - before.proof.y;
            return top + box.height;
          }));
        const proofHeight = Math.min(before.proof.height, contentBottom > 0
          ? contentBottom + (parseFloat(proofStyle.paddingBottom) || 0)
          : before.proof.height);
        const unusedProofSpace = Math.max(0, before.proof.height - proofHeight);
        const ctaGap = before.cta.y - before.proof.bottom;
        const gapReclaim = Math.max(0, ctaGap - 8);
        const ctaGapDeficit = Math.max(0, 8 - ctaGap);
        // Match the native space above the rating below the actual quote line,
        // rather than adding space after the unused legacy proof container.
        const nativeReviewGap = nodes.summary ? rect(nodes.summary).y - before.product.bottom : NaN;
        const reviewDockGap = Number.isFinite(nativeReviewGap) && nativeReviewGap > 0 ? nativeReviewGap : 8;
        const insertionHeight = rowHeight + reviewDockGap;
        const needed = Math.max(0, insertionHeight - unusedProofSpace - gapReclaim + ctaGapDeficit);
        const margin = headerSlack + growthRoom - needed;
        const previous = this.mode;
        this.mode = margin >= -0.1 ? 'full' : 'compact';
        this.host.setAttribute('data-m1-dock-layout', this.mode);

        const reclaim = this.mode === 'full' ? Math.min(needed, headerSlack) : 0;
        const growth = this.mode === 'full' ? needed - reclaim : 0;
        if (this.mode === 'full') {
          this.set(nodes.panel, 'height', `${before.panel.height + growth}px`);
          this.set(nodes.panel, 'min-height', '0');
          this.set(nodes.panel, 'max-height', 'none');
          this.set(nodes.product, 'top', `${before.product.y - before.panel.y - reclaim}px`);
          this.set(nodes.product, 'bottom', 'auto');
          this.set(nodes.product, 'height', `${before.product.height}px`);
          this.set(nodes.visual, 'top', `${before.visual.y - before.product.y}px`);
          this.set(nodes.visual, 'bottom', 'auto');
          this.set(nodes.visual, 'height', `${before.visual.height}px`);
          this.set(nodes.image, 'width', `${before.image.width}px`);
          this.set(nodes.image, 'height', `${before.image.height}px`);
          this.set(nodes.promises, 'top', `${before.promises.y - before.panel.y - reclaim}px`);
          this.set(nodes.proof, 'top', `${before.proof.y - before.panel.y - reclaim}px`);
          this.set(nodes.proof, 'bottom', 'auto');
          this.set(nodes.proof, 'height', `${proofHeight}px`);
          this.set(nodes.dock, 'top', `${rect(nodes.proof).bottom + reviewDockGap - rect(nodes.panel).y}px`);
        } else {
          this.set(nodes.proof, 'display', 'none');
          this.set(nodes.dock, 'top', `${before.proof.y +
            (before.proof.height - rowHeight) / 2 - before.panel.y}px`);
          this.showCompactRating(nodes);
        }
        const after = Object.fromEntries([...required, 'dock'].map(key => [key, rect(nodes[key])]));
        this.metrics = { mode: this.mode, eligible: true, previous, viewport, before, after,
          capacity: { rowHeight, needed, headerSlack, growthRoom, margin, reclaim, growth,
            desiredTop, topLimit, proofHeight, unusedProofSpace, ctaGap, gapReclaim, ctaGapDeficit,
            nativeReviewGap, reviewDockGap, insertionHeight } };
        return this.metrics;
      } finally {
        this.running = false;
      }
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      if (this.frame) cancelAnimationFrame(this.frame);
      clearTimeout(this.viewportTimer);
      this.cleanup.forEach(remove => remove());
      this.cleanup = [];
      this.observer.disconnect();
      this.resizeObserver?.disconnect();
      this.restore();
      this.compactRating?.remove();
      this.safeArea.remove();
      this.mode = null;
    }
  }

  window.NexDockLayout = NexDockLayout;
})();
